import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { Observable, Subject } from "rxjs";
import { debounceTime, distinctUntilChanged, map, takeUntil } from "rxjs/operators";
import { Cnae, Dominio, Municipio } from "src/app/model/dominio-operacao.model";
import { OperacaoCreditoLivre, OutrasGarantias } from "src/app/model/operacao-credito-livre.model";
import { OperacaoGarantida } from "src/app/model/operacao-garantida.model";
import { TrancheService } from "src/app/shared/services/tranche.service";
import { Tranche } from "src/app/model/tranche.model";
import { ModalidadeService } from "src/app/shared/services/modalidade.service";
import { ControleHabilitacaoCamposService } from "../../../controle-habilitacao-campos.service";
import { MODALIDADE_GARANTIA_FGBS, MODALIDADE_GARANTIA_FGT } from "src/app/model/modalidade_garantia.model";
import {Generos, GenerosEnum, GenerosLista} from '../../../../../../model/generos-enum';

@Component({
    selector: 'aba-cliente',
    templateUrl: 'aba-cliente.component.html',
    standalone: false
})
export class AbaClienteComponent implements OnChanges, OnInit, OnDestroy {

    private readonly destroy$ = new Subject();
    @Input()
    public operacao: OperacaoCreditoLivre;
    @Input()
    public modoEdicao: Boolean;
    @Input()
    public modoAditivo: Boolean;
    @Input()
    public emInclusao: Boolean;
    @Input()
    public dadosDominio: Dominio;
    @Input()
    public model: OperacaoGarantida;
    @Input()
    public modoRetificacao: Boolean;
    @Input()
    public modalidadeGarantia: string;
    @Input()
    public alteracaoClienteFinalAditivo: Boolean;

    public outrasGarantias: OutrasGarantias[];

    public tranche: Tranche;
    public exibeMEI: boolean;
    public modalidadeSebrae: boolean;
    public exibirCampoIndicadorPessoaDeficiencia: boolean;

    public uuidPattern: string = "[0-9a-fA-F]{8}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{4}\-[0-9a-fA-F]{12}$";
    public generoSocios: Generos[] = GenerosLista;

    constructor(private trancheService: TrancheService,
        private controleHabilitacaoCamposService: ControleHabilitacaoCamposService,
        private modalidadeService: ModalidadeService) {
    }

    ngOnInit(): void {
        this.trancheService.getTrancheAtual().pipe(takeUntil(this.destroy$)).subscribe((tranche) => {
            this.tranche = tranche;
        });

        this.modalidadeService.modalidade$.pipe(takeUntil(this.destroy$)).subscribe((modalidade) => {
            const ehFGT = modalidade === MODALIDADE_GARANTIA_FGT;
            const ehFGBS = modalidade === MODALIDADE_GARANTIA_FGBS;

            this.exibeMEI = (ehFGT || ehFGBS);
            this.modalidadeSebrae =  ehFGBS;

            this.exibirCampoIndicadorPessoaDeficiencia = (ehFGT || ehFGBS);
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.outrasGarantias = this.operacao.outrasGarantias;
    }

    ngOnDestroy(): void {
        this.destroy$.next(undefined);
        this.destroy$.complete();
    }

    adicionarGarantia() {
        const operacaoCreditoLivre: OperacaoCreditoLivre = <OperacaoCreditoLivre>this.model;
        operacaoCreditoLivre.outrasGarantias.push(new OutrasGarantias());
    }

    removerGarantia(i) {
        const operacaoCreditoLivre: OperacaoCreditoLivre = <OperacaoCreditoLivre>this.model;
        operacaoCreditoLivre.outrasGarantias.splice(i, 1);
    }

    atribuiGarantia(tipoGarantia: string, i) {
        const operacaoCreditoLivre: OperacaoCreditoLivre = <OperacaoCreditoLivre>this.model;
        operacaoCreditoLivre.outrasGarantias[i].tipoGarantia = tipoGarantia;
    }

    atribuiCnaeClienteFinal(obj) {
        this.model.cnaeClienteFinal = obj.item.codigo;
        this.model.detalhe.nomeCnaeClienteFinal = obj.item.nome;
    }

    atribuiCnaeEnquadramento(obj) {
        this.model.cnaeEnquadramento = obj.item.codigo;
        this.model.detalhe.nomeCnaeEnquadramento = obj.item.nome;
    }

    atribuiCnaeGrandeEmpresa(obj) {
        (<OperacaoCreditoLivre>this.model).cnaePortariaGrandeEmpresa = obj.item.codigo;
        (<OperacaoCreditoLivre>this.model).detalheCreditoLivre.nomeCnaePortariaGrandeEmpresa = obj.item.nome;
    }

    inspecionaCnaeGrandeEmpresa(obj) {
        if (obj?.target?.value.trim().length == 0) {
            (<OperacaoCreditoLivre>this.model).cnaePortariaGrandeEmpresa = null;
            (<OperacaoCreditoLivre>this.model).detalheCreditoLivre.nomeCnaePortariaGrandeEmpresa = "";
        }
    }

    atribuiMunicipioClienteFinal(obj) {
        (<OperacaoCreditoLivre>this.model).enderecoSedeClienteFinal.codigoIbgeMunicipio = obj.item.id;
        (<OperacaoCreditoLivre>this.model).enderecoSedeClienteFinal.nomeMunicipio = obj.item.nome;
    }

    atribuirNovoValorPCD(evento) {
        if (evento !== 'S') {
            this.operacao.indicadorPessoaComDeficiencia = '';
        }
    }

    atribuirNovoValorGenero(evento) {
      this.operacao.generoSocioBeneficiario = evento;
    }




    procuraCnae = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term => term.length < 2 ? []
                : this.dadosDominio.cnaes.filter(v => v.nome.toLowerCase().indexOf(term.toLowerCase()) > -1
                    || v.codigo.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10))
        );

    formataCnae = (result: Cnae) => result.codigo + " - " + result.nome;

    procuraMunicipio = (text$: Observable<string>) =>
        text$.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            map(term => term.length < 2 ? []
                : this.dadosDominio.municipios.filter(v => v.nome.toLowerCase().indexOf(term.toLowerCase()) > -1).slice(0, 10))
        );

    formataMunicipio = (result: Municipio) => result.nome;

    campoHabilitado(nomeCampo: string) {
      return this.emInclusao || (this.modoEdicao && (this.passivelDeRetificacao(nomeCampo) || this.alteracaoClienteFinalAditivo || this.passivelDeAditivo(nomeCampo)));
    }

    passivelDeRetificacao(nomeCampo: string) {
      return this.modoRetificacao && this.controleHabilitacaoCamposService.passivelDeRetificacao(nomeCampo, this.modalidadeGarantia);
    }

    passivelDeAditivo(nomeCampo: string) {
      return this.modoAditivo && this.controleHabilitacaoCamposService.passivelDeAditivo(nomeCampo, this.modalidadeGarantia);
  }

  getNomeGenero(ideGenero) {
    if (ideGenero != null) {
      return GenerosLista.find(g => `${g.valor}` === ideGenero).nome;
    }
    return null
  }

}
